import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Table, Button, Spin, Alert, Descriptions, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface ClasificacionHVI {
  id: number;
  SCI: number;
  MST: number;
  UHML: number;
  ML: number;
  UI: number;
  STR: number;
  ELG: number;
  MIC: number;
  AMT: number;
  RD: number;
  B: number;
  CG: string;
  T_CNT: number;
  T_AREA: number;
  MR: number;
  SFI: number;
  TRID: number;
  alta: number;
}

interface ClasificacionDetalle {
  id: number;
  numero_lote: number;
  numero_fardo: number;
  calidad: string;
  calidad_numerica: number;
  alta: number;
}

interface ClasificacionInfo {
  encontrada: boolean;
  id?: number;
  numero_planilla?: number;
  fecha?: string;
  observaciones?: string;
  IA?: string;
  hvi?: string;
}

interface FardoComparacion {
  fardo_orden: {
    id: number;
    nro_fardo: number;
    nro_lote: number;
    origen_id: number;
    origen_nombre: string;
    cliente_id: number;
    cliente_nombre: string;
    empresa_id: number;
    sucursal_id: number;
    silobolsa: number;
    partesilobolsa: number;
  };
  clasificacion_mismo_origen_cliente?: ClasificacionInfo;
  clasificacion_origen_origen?: ClasificacionInfo;
  clasificacion_detalle?: ClasificacionDetalle[];
  clasificacion_hvi?: ClasificacionHVI;
  clasificacion_detalle_origen?: ClasificacionDetalle[];
  clasificacion_hvi_origen?: ClasificacionHVI;
}

interface EmpresaData {
  id: number;
  denominacion: string;
  direccion: string;
  telefono: string;
  email: string;
  localidad: string | null;
  civa: string | null;
  actividad: string | null;
}

interface SucursalData {
  id: number;
  denominacion: string;
  direccion: string;
  telefono: string;
  email: string;
  localidad: string | null;
}

interface OrdenData {
  id: number;
  numero_orden: number;
  fecha: string;
  origen: {
    id: number;
    denominacion: string;
  } | null;
  cliente: {
    id: number;
    denominacion: string;
  } | null;
}

interface ClasificacionData {
  id_empresa: number;
  id_sucursal: number;
  id_orden: number;
  total_fardos: number;
  empresa: EmpresaData | null;
  sucursal: SucursalData | null;
  orden: OrdenData | null;
  comparacion: FardoComparacion[];
}

interface TableRow {
  key: string;
  nro_fardo: number;
  numero_planilla?: number;
  fecha?: string;
  calidad?: string;
  SCI?: number;
  MST?: number;
  UHML?: number;
  ML?: number;
  UI?: number;
  STR?: number;
  ELG?: number;
  MIC?: number;
  AMT?: number;
  RD?: number;
  B?: number;
  CG?: string;
  T_CNT?: number;
  T_AREA?: number;
  MR?: number;
  SFI?: number;
  TRID?: number;
}

const ClasificacionOrdenes: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { id_orden, nro_orden, id_sucursal } = location.state || {};

  const [data, setData] = useState<ClasificacionData | null>(null);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !user) {
        setError('Usuario no autenticado');
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const id_empresa = user?.id_empresa;

        const response = await fetch(`${process.env.REACT_APP_ORDENES_URL}/comparacion_clasificacion/${id_empresa}/${id_sucursal}/${id_orden}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Error al cargar los datos de clasificación');
        }

        const result: ClasificacionData = await response.json();
        setData(result);

        // Procesar datos para la tabla
        const rows: TableRow[] = [];
        
        result.comparacion.forEach((fardo) => {
          // Fila para clasificación ORIGEN-CLIENTE
          if (fardo.clasificacion_mismo_origen_cliente?.encontrada) {
            const calidad = fardo.clasificacion_detalle && fardo.clasificacion_detalle.length > 0
              ? fardo.clasificacion_detalle[0].calidad
              : undefined;

            rows.push({
              key: `${fardo.fardo_orden.id}-origen-cliente`,
              nro_fardo: fardo.fardo_orden.nro_fardo,
              numero_planilla: fardo.clasificacion_mismo_origen_cliente.numero_planilla,
              fecha: fardo.clasificacion_mismo_origen_cliente.fecha,
              calidad,
              SCI: fardo.clasificacion_hvi?.SCI,
              MST: fardo.clasificacion_hvi?.MST,
              UHML: fardo.clasificacion_hvi?.UHML,
              ML: fardo.clasificacion_hvi?.ML,
              UI: fardo.clasificacion_hvi?.UI,
              STR: fardo.clasificacion_hvi?.STR,
              ELG: fardo.clasificacion_hvi?.ELG,
              MIC: fardo.clasificacion_hvi?.MIC,
              AMT: fardo.clasificacion_hvi?.AMT,
              RD: fardo.clasificacion_hvi?.RD,
              B: fardo.clasificacion_hvi?.B,
              CG: fardo.clasificacion_hvi?.CG,
              T_CNT: fardo.clasificacion_hvi?.T_CNT,
              T_AREA: fardo.clasificacion_hvi?.T_AREA,
              MR: fardo.clasificacion_hvi?.MR,
              SFI: fardo.clasificacion_hvi?.SFI,
              TRID: fardo.clasificacion_hvi?.TRID,
            });
          }

          // Fila para clasificación ORIGEN-ORIGEN
          if (fardo.clasificacion_origen_origen?.encontrada) {
            const calidad_origen = fardo.clasificacion_detalle_origen && fardo.clasificacion_detalle_origen.length > 0
              ? fardo.clasificacion_detalle_origen[0].calidad
              : undefined;

            rows.push({
              key: `${fardo.fardo_orden.id}-origen-origen`,
              nro_fardo: fardo.fardo_orden.nro_fardo,
              numero_planilla: fardo.clasificacion_origen_origen.numero_planilla,
              fecha: fardo.clasificacion_origen_origen.fecha,
              calidad: calidad_origen,
              SCI: fardo.clasificacion_hvi_origen?.SCI,
              MST: fardo.clasificacion_hvi_origen?.MST,
              UHML: fardo.clasificacion_hvi_origen?.UHML,
              ML: fardo.clasificacion_hvi_origen?.ML,
              UI: fardo.clasificacion_hvi_origen?.UI,
              STR: fardo.clasificacion_hvi_origen?.STR,
              ELG: fardo.clasificacion_hvi_origen?.ELG,
              MIC: fardo.clasificacion_hvi_origen?.MIC,
              AMT: fardo.clasificacion_hvi_origen?.AMT,
              RD: fardo.clasificacion_hvi_origen?.RD,
              B: fardo.clasificacion_hvi_origen?.B,
              CG: fardo.clasificacion_hvi_origen?.CG,
              T_CNT: fardo.clasificacion_hvi_origen?.T_CNT,
              T_AREA: fardo.clasificacion_hvi_origen?.T_AREA,
              MR: fardo.clasificacion_hvi_origen?.MR,
              SFI: fardo.clasificacion_hvi_origen?.SFI,
              TRID: fardo.clasificacion_hvi_origen?.TRID,
            });
          }

          // Si no hay clasificaciones, agregar una fila indicándolo
          if (!fardo.clasificacion_mismo_origen_cliente?.encontrada && !fardo.clasificacion_origen_origen?.encontrada) {
            rows.push({
              key: `${fardo.fardo_orden.id}-sin-clasificacion`,
              nro_fardo: fardo.fardo_orden.nro_fardo,
            });
          }
        });

        setTableData(rows);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Error desconocido');
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, user, isAuthenticated, id_orden, id_sucursal]);

  const handleVolver = () => {
    navigate('/ordenes/listado');
  };

  // Función para comparar valores y determinar si son diferentes
  const getCellStyle = (record: TableRow, dataIndex: string, value: any) => {
    // Solo aplicar comparación desde "Calidad" hacia la derecha
    const comparisonColumns = ['calidad', 'SCI', 'MST', 'UHML', 'ML', 'UI', 'STR', 'ELG', 'MIC', 'AMT', 'RD', 'B', 'CG', 'T_CNT', 'T_AREA', 'MR', 'SFI', 'TRID'];
    
    if (!comparisonColumns.includes(dataIndex)) {
      return {};
    }

    // Buscar otras filas del mismo fardo
    const sameFardoRows = tableData.filter(row => row.nro_fardo === record.nro_fardo);
    
    if (sameFardoRows.length <= 1) {
      return {};
    }

    // Comparar con otras filas del mismo fardo
    const hasDifference = sameFardoRows.some(otherRow => {
      if (otherRow.key === record.key) return false;
      
      const otherValue = otherRow[dataIndex as keyof TableRow];
      
      // Comparar valores (considerando null/undefined como iguales)
      if (value === null || value === undefined) {
        return otherValue !== null && otherValue !== undefined;
      }
      if (otherValue === null || otherValue === undefined) {
        return true;
      }
      
      // Comparar números con tolerancia
      if (typeof value === 'number' && typeof otherValue === 'number') {
        return Math.abs(value - otherValue) > 0.001;
      }
      
      // Comparar strings
      return String(value) !== String(otherValue);
    });

    return hasDifference ? { backgroundColor: '#ffb3d1' } : {};
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Nro Fardo',
      dataIndex: 'nro_fardo',
      key: 'nro_fardo',
      width: 100,
      fixed: 'left',
    },
    {
      title: 'Nro Planilla',
      dataIndex: 'numero_planilla',
      key: 'numero_planilla',
      width: 100,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 110,
    },
    {
      title: 'Calidad',
      dataIndex: 'calidad',
      key: 'calidad',
      width: 80,
      render: (val: any, record: TableRow) => (
        <span style={getCellStyle(record, 'calidad', val)}>
          {val}
        </span>
      ),
    },
    {
      title: 'SCI',
      dataIndex: 'SCI',
      key: 'SCI',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'SCI', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'MST',
      dataIndex: 'MST',
      key: 'MST',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'MST', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'UHML',
      dataIndex: 'UHML',
      key: 'UHML',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'UHML', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'ML',
      dataIndex: 'ML',
      key: 'ML',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'ML', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'UI',
      dataIndex: 'UI',
      key: 'UI',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'UI', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'STR',
      dataIndex: 'STR',
      key: 'STR',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'STR', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'ELG',
      dataIndex: 'ELG',
      key: 'ELG',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'ELG', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'MIC',
      dataIndex: 'MIC',
      key: 'MIC',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'MIC', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'AMT',
      dataIndex: 'AMT',
      key: 'AMT',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'AMT', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'RD',
      dataIndex: 'RD',
      key: 'RD',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'RD', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'B',
      dataIndex: 'B',
      key: 'B',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'B', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'CG',
      dataIndex: 'CG',
      key: 'CG',
      width: 70,
      render: (val: string, record: TableRow) => (
        <span style={getCellStyle(record, 'CG', val)}>
          {val}
        </span>
      ),
    },
    {
      title: 'T_CNT',
      dataIndex: 'T_CNT',
      key: 'T_CNT',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'T_CNT', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'T_AREA',
      dataIndex: 'T_AREA',
      key: 'T_AREA',
      width: 80,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'T_AREA', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'MR',
      dataIndex: 'MR',
      key: 'MR',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'MR', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'SFI',
      dataIndex: 'SFI',
      key: 'SFI',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'SFI', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'TRID',
      dataIndex: 'TRID',
      key: 'TRID',
      width: 70,
      render: (val: number, record: TableRow) => (
        <span style={getCellStyle(record, 'TRID', val)}>
          {val?.toFixed(2)}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Cargando clasificaciones..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleVolver}>
              Volver
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert message="No se encontraron datos" type="warning" showIcon />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%' }}>
      
      {/* Header con botón volver */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleVolver}>
          Volver
        </Button>
      </div>

      {/* Título */}
      <div style={{
        color: 'white',
        backgroundColor: '#1890ff',
        marginBottom: '20px',
        textAlign: 'center',
        padding: '15px',
        borderRadius: '5px',
        textTransform: 'uppercase',
        fontSize: '24px',
        fontWeight: 'bold',
      }}>
        VER CLASIFICACIÓN
      </div>

      {/* Carátula - Información General */}
      <Card 
        title={<div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>CARÁTULA</div>}
        style={{ marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Descriptions column={2} bordered>
          {/* Información de Empresa */}
          <Descriptions.Item label="Empresa" span={2}>
            <div>
              <Text strong style={{ fontSize: '16px' }}>
                {data.empresa?.denominacion || `ID: ${data.id_empresa}`}
              </Text>
              {data.empresa && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Dirección: {data.empresa.direccion}</div>
                  <div>Teléfono: {data.empresa.telefono}</div>
                  <div>Email: {data.empresa.email}</div>
                  {data.empresa.localidad && <div>Localidad: {data.empresa.localidad}</div>}
                  {data.empresa.civa && <div>Tipo Responsable: {data.empresa.civa}</div>}
                  {data.empresa.actividad && <div>Actividad: {data.empresa.actividad}</div>}
                </div>
              )}
            </div>
          </Descriptions.Item>

          {/* Información de Sucursal */}
          <Descriptions.Item label="Sucursal" span={2}>
            <div>
              <Text strong style={{ fontSize: '16px' }}>
                {data.sucursal?.denominacion || `ID: ${data.id_sucursal}`}
              </Text>
              {data.sucursal && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Dirección: {data.sucursal.direccion}</div>
                  <div>Teléfono: {data.sucursal.telefono}</div>
                  <div>Email: {data.sucursal.email}</div>
                  {data.sucursal.localidad && <div>Localidad: {data.sucursal.localidad}</div>}
                </div>
              )}
            </div>
          </Descriptions.Item>

          {/* Información de Orden */}
          <Descriptions.Item label="Orden" span={2}>
            <div>
              <Text strong style={{ fontSize: '16px' }}>
                Nro: {data.orden?.numero_orden || nro_orden} (ID: {data.id_orden})
              </Text>
              {data.orden && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Fecha: {data.orden.fecha}</div>
                  {data.orden.origen && (
                    <div>Origen: {data.orden.origen.denominacion}</div>
                  )}
                  {data.orden.cliente && (
                    <div>Cliente: {data.orden.cliente.denominacion}</div>
                  )}
                </div>
              )}
            </div>
          </Descriptions.Item>

          {/* Total Fardos */}
          <Descriptions.Item label="Total Fardos" span={2}>
            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
              {data.total_fardos}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Cards agrupadas por Fardo */}
      <Card
        title={<div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
          CLASIFICACIONES DE FARDOS
        </div>}
        style={{ marginBottom: '20px' }}
      >
        {(() => {
          // Agrupar datos por número de fardo
          const groupedData = tableData.reduce((acc, row) => {
            const fardoNum = row.nro_fardo;
            if (!acc[fardoNum]) {
              acc[fardoNum] = [];
            }
            acc[fardoNum].push(row);
            return acc;
          }, {} as Record<number, TableRow[]>);

          return Object.entries(groupedData).map(([fardoNum, rows]) => (
            <Card
              key={fardoNum}
              style={{
                marginBottom: '16px',
                border: '2px solid #1890ff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
              }}
            >
              <Table
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 2500 }}
                size="small"
                bordered
                style={{ margin: 0 }}
              />
            </Card>
          ));
        })()}
      </Card>

      {/* Botón volver al final */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Button onClick={handleVolver} type="primary" size="small">
          Volver al Listado
        </Button>
      </div>
    </div>
  );
};

export default ClasificacionOrdenes;
